import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Injector,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { FieldType, FieldTypeConfig, FormlyModule } from '@ngx-formly/core';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';

import { AreaOfInterestService } from '../../../api/services/area-of-interest.service';
import { JobPostingService } from '../../../api/services/job-posting.service';
import { MatchCompetencyService } from '../../../api/services/match-competency.service';

type FactorKey = 'companyCultureFactor' | 'personalityFactor' | 'areaOfInterestFactor';

interface SectionDef {
  title: string;
  factorKey: FactorKey;
  childIndex: number;
}

/**
 * Composite Formly field that owns the Match tab's three sections. Each
 * section groups its percentage weight with its associated picker:
 *   • Company culture — weight + competency picker (max 5)
 *   • Personality     — weight + competency picker (max 2)
 *   • Areas of interest — weight + area-of-interests selector (max 15)
 *
 * The three factor FormControls live on this component's FormGroup and
 * sum-cap at 100. The three child fields (in `defaultOptions.fieldGroup`)
 * carry their own state.
 *
 * Loads the existing match criterion for `props.entityId` on init (edit
 * mode). For create mode (no entityId), starts empty. Save is owned by
 * the caller's save pipeline, which reads the matchcriterion value off
 * the model.
 *
 * Usage:
 * ```
 * { key: 'matchcriterion', type: 'match-criterion', props: { entityId } }
 * ```
 */
const MAX_COMPANY_CULTURE_COMPETENCIES = 5;
const MAX_PERSONALITY_COMPETENCIES = 2;
const MAX_AREAS_OF_INTEREST = 15;

const SECTIONS: readonly SectionDef[] = [
  { title: _('Company culture'), factorKey: 'companyCultureFactor', childIndex: 0 },
  { title: _('Personality'), factorKey: 'personalityFactor', childIndex: 1 },
  { title: _('Areas of interest'), factorKey: 'areaOfInterestFactor', childIndex: 2 },
];

const FACTOR_KEYS: readonly FactorKey[] = SECTIONS.map((s) => s.factorKey);

@Component({
  selector: 'app-formly-match-criterion',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatProgressSpinnerModule,
    TranslateModule,
    FormlyModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (loading()) {
      <div class="flex items-center justify-center py-8">
        <mat-spinner diameter="32"></mat-spinner>
      </div>
    } @else {
      <div class="flex flex-col gap-5">
        @for (section of sections; track section.factorKey) {
          <section class="bg-white rounded-2xl px-6 py-5 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_2px_8px_rgba(0,0,0,0.03)]">
            <h3 class="font-alternate text-[18px] font-semibold text-black m-0 mb-4">
              {{ section.title | translate }}
            </h3>
            <div class="flex items-center gap-4 mb-4">
              <label
                class="flex-1 font-base text-sm text-black/80"
                [attr.for]="section.factorKey">
                {{ 'Weighting percentage' | translate }}
              </label>
              <div class="relative w-32">
                <input
                  [class]="factorInputClass(section.factorKey)"
                  style="font-family: 'DM Sans', sans-serif;"
                  type="number"
                  min="0"
                  max="100"
                  [id]="section.factorKey"
                  [value]="fg.get(section.factorKey)?.value ?? 0"
                  (input)="onFactorChange(section.factorKey, $event)"
                  (blur)="fg.get(section.factorKey)?.markAsTouched()" />
                <span
                  class="absolute right-3 top-1/2 -translate-y-1/2 text-sm"
                  style="color: var(--color-mty-primary); font-weight: 600;">%</span>
              </div>
            </div>
            @if (field.fieldGroup?.[section.childIndex]; as child) {
              <formly-field [field]="child"></formly-field>
            }
          </section>
        }
      </div>
    }
  `,
})
export class MatchCriterionFieldComponent
  extends FieldType<FieldTypeConfig>
  implements OnInit
{
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);
  private readonly jobPostingService = inject(JobPostingService);

  readonly loading = signal(false);
  readonly sections = SECTIONS;

  override defaultOptions = {
    fieldGroup: [
      {
        key: 'companyCultureCompetencies',
        type: 'competency-picker',
        props: {
          maxSelections: MAX_COMPANY_CULTURE_COMPETENCIES,
          selectPrompt: _('Select maximum 5 competences'),
          sortPrompt: _('Sort the selected competences based on priority'),
          loadOptions: () =>
            inject(MatchCompetencyService)
              .getMatchCompetencies({ PageNumber: 1, PageSize: 500 })
              .pipe(
                map((res) =>
                  (res?.data ?? []).filter(
                    (c) => (c as { type?: string }).type === 'CompanyCulture',
                  ),
                ),
              ),
        },
      },
      {
        key: 'personalityCompetencies',
        type: 'competency-picker',
        props: {
          maxSelections: MAX_PERSONALITY_COMPETENCIES,
          selectPrompt: _('Select maximum 2 competences'),
          sortPrompt: _('Sort the selected competences based on priority'),
          loadOptions: () =>
            inject(MatchCompetencyService)
              .getMatchCompetencies({ PageNumber: 1, PageSize: 500 })
              .pipe(
                map((res) =>
                  (res?.data ?? []).filter(
                    (c) => (c as { type?: string }).type === 'Personality',
                  ),
                ),
              ),
        },
      },
      {
        key: 'areaOfInterests',
        type: 'core-selector-areaofinterests',
        props: {
          maxSelections: MAX_AREAS_OF_INTEREST,
          loadOptions: () =>
            inject(AreaOfInterestService)
              .getAreaOfInterests({ PageNumber: 1, PageSize: 200 })
              .pipe(map((r) => r?.data ?? [])),
        },
      },
    ],
  };

  get fg(): FormGroup {
    return this.formControl as unknown as FormGroup;
  }

  ngOnInit(): void {
    for (const k of FACTOR_KEYS) {
      if (!this.fg.contains(k)) {
        this.fg.addControl(k, new FormControl(0), { emitEvent: false });
      }
    }

    const entityId = this.props['entityId'] as string | undefined;
    if (!entityId) return;

    this.loading.set(true);
    this.jobPostingService
      .getJobPostingMatchCriterion({ jobPostingId: entityId })
      .pipe(catchError(() => of(null)), takeUntilDestroyed(this.destroyRef))
      .subscribe((mc) => {
        this.loading.set(false);
        if (!mc) return;
        this.fg.patchValue(mc);
      });
  }

  onFactorChange(key: FactorKey, event: Event): void {
    const input = event.target as HTMLInputElement;
    const newValue = parseInt(input.value, 10) || 0;
    const othersTotal = FACTOR_KEYS.reduce(
      (sum, k) => (k === key ? sum : sum + ((this.fg.get(k)?.value as number) ?? 0)),
      0,
    );
    const capped = Math.max(0, Math.min(newValue, 100 - othersTotal));
    const ctl = this.fg.get(key);
    ctl?.setValue(capped);
    ctl?.markAsDirty();
  }

  factorInputClass(key: FactorKey): string {
    const ctl = this.fg.get(key);
    const base = 'w-full px-3 py-2 pr-10 rounded-lg border text-right transition-colors focus:outline-none focus:ring-2';
    const invalid = !!ctl && ctl.invalid && (ctl.dirty || ctl.touched);
    return invalid
      ? `${base} border-mty-danger focus:ring-mty-danger/15`
      : `${base} border-black/10 focus:border-mty-primary focus:ring-mty-primary/15`;
  }
}
